/**
 * エラー監視（Sentry）+ アクセス解析（GA4）の初期化とイベント送信
 *
 * 環境変数が未設定の場合はすべて no-op になる（ローカル開発・テストで安全）:
 * - REACT_APP_SENTRY_DSN: Sentry プロジェクトの DSN
 * - REACT_APP_GA4_MEASUREMENT_ID: GA4 測定 ID（G-XXXXXXXXXX）
 */

import * as Sentry from "@sentry/react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";
const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function isSentryEnabled(): boolean {
  return SENTRY_DSN !== "";
}

export function isGAEnabled(): boolean {
  return GA4_ID !== "";
}

/** Sentry 初期化（DSN 未設定なら何もしない） */
export function initSentry(): void {
  if (!isSentryEnabled()) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    // ブラウザ拡張等に起因するノイズを除外
    ignoreErrors: ["ResizeObserver loop", "Non-Error promise rejection captured"],
    // OAuth 認可コード等を Sentry に残さない
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = event.request.url.replace(
          /([?&])(code|state)=[^&]+/g,
          "$1$2=[FILTERED]"
        );
      }
      return event;
    },
  });
}

/** GA4 初期化（測定 ID 未設定なら何もしない）。ページビューは手動送信（SPA対応） */
export function initGA(): void {
  if (!isGAEnabled()) return;
  if (window.gtag) return; // 二重初期化防止

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // GA4 は arguments オブジェクトそのものを要求する（配列化すると計測されない）
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  // SPA のためページビューはルート変更時に trackPageview で送る
  window.gtag("config", GA4_ID, {
    send_page_view: false,
    // config 時点の document.location（?code= 付きの可能性）を送らない
    page_location: sanitizedLocation(window.location.pathname, window.location.search),
  });
}

/** アプリ起動時にまとめて初期化 */
export function initMonitoring(): void {
  initSentry();
  initGA();
}

/** GA に送ってよいクエリパラメータ（流入元計測用の UTM のみ） */
export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/** search から UTM 5種のうち値が空でないものを取り出す（値は200文字で切る） */
export function pickUtmParams(search: string): Record<string, string> {
  const result: Record<string, string> = {};
  try {
    const params = new URLSearchParams(search);
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) result[key] = value.slice(0, 200);
    }
  } catch {
    // 不正な search は UTM なし扱い
  }
  return result;
}

/**
 * GA に送ってよい location（origin + pathname + utm_* のみ）。
 * OAuth 復帰直後の ?code=&state= を GA に送らないため、イベント送信側でも必ず通す。
 */
export function sanitizedLocation(pathname: string, search: string): string {
  const utm = pickUtmParams(search);
  const kept = new URLSearchParams();
  for (const key of UTM_KEYS) {
    if (utm[key] !== undefined) kept.append(key, utm[key]);
  }
  const q = kept.toString();
  return window.location.origin + pathname + (q ? `?${q}` : "");
}

/** ページビュー送信（ルート変更時に呼ぶ） */
export function trackPageview(path: string, search: string = ""): void {
  if (!isGAEnabled() || !window.gtag) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      // OAuth 認可コード等を送らないため、utm_* 以外のクエリは捨てる
      // （明示指定で gtag 自動収集の document location を上書きする）
      page_location: sanitizedLocation(path, search),
    });
  } catch {
    // 計測失敗でアプリ本体を壊さない
  }
}

/** カスタムイベント送信（回答・Pro導線CV等） */
export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!isGAEnabled() || !window.gtag) return;
  try {
    // page_location を明示しないと gtag が document.location（?code= 付き）を拾うため既定で上書きする
    window.gtag("event", name, {
      page_location: sanitizedLocation(window.location.pathname, window.location.search),
      ...(params ?? {}),
    });
  } catch {
    // 計測失敗でアプリ本体を壊さない
  }
}

export { Sentry };
